/* @flow */
import cx from 'classnames';
import * as React from 'react';

import Icon from 'components/Icon';
import MediaObject from 'components/MediaObject';
import Text from 'components/Text';
import TooltipTrigger from 'components/TooltipTrigger';

import type { ColumnHeader, SortDirection } from '../ComposableTable';

import './ComposableTableHeader.scss';

const ICON_WIDTH = 16;
const OS_BROWSER_RENDERING_AFFORDANCE = 4;
const SORT_ICON_WIDTH = 16;
const TABLE_CELL_PADDING_X = 16;
const WIDTH_AFFORDANCE =
  ICON_WIDTH + SORT_ICON_WIDTH + TABLE_CELL_PADDING_X * 2 + OS_BROWSER_RENDERING_AFFORDANCE;

export type ComposableTableHeaderSortProps = {
  onClick: Function,
  sortColumn: ?string,
  sortDirection: ?SortDirection,
};

type Props = {
  columnHeader: ColumnHeader,
  sortProps?: ?ComposableTableHeaderSortProps,
  topOffset?: number,
  index: number,
  spreadsheet?: boolean,
};

export default class ComposableTableHeader extends React.Component<Props> {
  getNewSortDirection = () => {
    const { columnHeader, sortProps } = this.props;

    // flow necessitates
    if (!sortProps) {
      return '';
    }

    const { sortValue } = columnHeader;
    const { sortColumn, sortDirection } = sortProps;

    if (sortValue === sortColumn) {
      return sortDirection === 'asc' ? 'desc' : 'asc';
    }

    return 'asc';
  };

  addColumnLetters = (component: React.Element) => {
    // converts 0 = A, 1 = B, 2 = C, etc.
    const indexToAlphaLetter = (index: number) => String.fromCharCode(index + 65);

    return (
      <MediaObject center gutter="small">
        <Text color="eclipse">{indexToAlphaLetter(this.props.index)}</Text>
        {component}
      </MediaObject>
    );
  };

  handleClick = () => {
    const { columnHeader, sortProps } = this.props;

    // flow necessitates
    if (!sortProps) {
      return;
    }

    const { sortValue } = columnHeader;
    const { onClick } = sortProps;

    onClick(sortValue, this.getNewSortDirection());
  };

  renderHeaderText = () => {
    const { columnHeader } = this.props;
    const { icon, name } = columnHeader;
    return (
      <MediaObject center figure="right" gutter="xxsmall">
        <Text data-test="column-header">{name}</Text>
        {icon ? (
          <TooltipTrigger tooltip={icon.tooltip} placement="top">
            <span>
              <Icon name={icon.iconName} size="inherit" />
            </span>
          </TooltipTrigger>
        ) : (
          <span />
        )}
      </MediaObject>
    );
  };

  render() {
    const { columnHeader, sortProps, topOffset, spreadsheet } = this.props;
    const { sortValue, type, width } = columnHeader;

    const style = {};
    if (width) {
      style.width = width + WIDTH_AFFORDANCE;
    }
    if (topOffset) {
      style.top = topOffset;
    }

    return (
      <th
        key={columnHeader.name}
        style={style}
        tabIndex={sortValue ? 0 : null}
        className={cx(
          'ComposableTableHeader',
          sortValue && 'ComposableTableHeader--sortable',
          type && `ComposableTableHeader--${type}`
        )}
        onClick={sortProps ? this.handleClick : null}>
        <MediaObject center figure="right" gutter="xxsmall" inline>
          {spreadsheet ? this.addColumnLetters(this.renderHeaderText()) : this.renderHeaderText()}
          {sortProps &&
            sortProps.sortColumn === sortValue && (
              <Icon
                name={sortProps.sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'}
                size="inherit"
              />
            )}
        </MediaObject>
      </th>
    );
  }
}
